import React from 'react';
import { ArrowRightIcon, BrainIcon, CodeIcon, HeartIcon, LineChartIcon, UsersIcon } from 'lucide-react';
import { Button } from '../components/Button';
import { SectionHeading } from '../components/SectionHeading';
export function About() {
  return <div className="bg-cream" data-id="element-80">
      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-white" data-id="element-81">
        <div className="container mx-auto px-6" data-id="element-82">
          <div className="flex flex-col md:flex-row items-center" data-id="element-83">
            <div className="md:w-1/3 mb-10 md:mb-0" data-id="element-84">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/a7DBQuKX5S57sJrirQcAv7/headshot.jpg" alt="Emma Rose" className="w-full h-auto rounded-lg shadow-lg" data-id="element-85" />
            </div>
            <div className="md:w-2/3 md:pl-12" data-id="element-86">
              <h1 className="text-3xl md:text-4xl font-display font-bold text-navy-dark mb-4" data-id="element-87">
                Emma Rose
              </h1>
              <h2 className="text-xl text-coral-dark mb-6" data-id="element-88">
                Senior Product Leader | Open to New Opportunities
              </h2>
              <p className="text-navy mb-6" data-id="element-89">
                With 8+ years in product management across health, nutrition,
                fitness, and wellness, I've built a career combining deep domain
                expertise with technical product leadership. My background in
                nutrition science gives me a unique perspective when building
                health-tech products that truly serve users' needs and make
                wellness more accessible.
              </p>
              <p className="text-navy mb-6" data-id="element-90">
                I'm deeply mission-driven, passionate about democratising health
                and wellness through technology. Every product I build aims to
                remove barriers and empower people to live healthier lives,
                regardless of their circumstances.
              </p>
              <div className="flex flex-wrap gap-4" data-id="element-91">
                <Button href="/contact" data-id="element-92">Get in Touch</Button>
                <Button href="https://linkedin.com/in/emma-rose" variant="outline" external data-id="element-93">
                  LinkedIn Profile
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* My Journey Section */}
      <section className="py-16 bg-cream" data-id="element-94">
        <div className="container mx-auto px-6" data-id="element-95">
          <SectionHeading title="My Journey: From Nutritionist to CPO" subtitle="How my background in nutrition science became my product leadership superpower" data-id="element-96" />
          <div className="bg-white rounded-lg shadow-md p-8 mb-12" data-id="element-97">
            <div className="space-y-8" data-id="element-98">
              <div data-id="element-99">
                <h3 className="text-xl font-display font-semibold text-navy-dark mb-3" data-id="element-100">
                  The Foundation: BSc Human Nutrition & Physiology
                </h3>
                <p className="text-navy" data-id="element-101">
                  My career began with a deep scientific understanding of how
                  nutrition affects the human body. This foundation has been
                  invaluable when building health-tech products, allowing me to
                  bridge the gap between scientific validity and user
                  experience.
                </p>
              </div>
              <div data-id="element-102">
                <h3 className="text-xl font-display font-semibold text-navy-dark mb-3" data-id="element-103">
                  The Transition: From Science to Product
                </h3>
                <p className="text-navy" data-id="element-104">
                  When I joined Fresh Fitness Food, I quickly saw how technology
                  could scale personalised nutrition. I began working with
                  developers to build tools that would automate our nutrition
                  planning processes, discovering my passion for product
                  development.
                </p>
              </div>
              <div data-id="element-105">
                <h3 className="text-xl font-display font-semibold text-navy-dark mb-3" data-id="element-106">
                  The Evolution: Building a Complete Platform
                </h3>
                <p className="text-navy" data-id="element-107">
                  As I moved into product leadership, I led the development of a
                  comprehensive ERP system that transformed our operations. My
                  nutrition background meant I could design algorithms and
                  workflows that truly reflected the science while meeting
                  business needs.
                </p>
              </div>
              <div data-id="element-108">
                <h3 className="text-xl font-display font-semibold text-navy-dark mb-3" data-id="element-109">
                  The Leadership: Becoming CPO
                </h3>
                <p className="text-navy" data-id="element-110">
                  As Chief Product Officer, I combined my domain expertise with
                  product leadership to guide our technology strategy. I've led
                  teams through multiple acquisitions, built 0→1 products, and
                  scaled operations through technology, always with the mission
                  of making quality nutrition more accessible.
                </p>
              </div>
              <div data-id="element-111">
                <h3 className="text-xl font-display font-semibold text-navy-dark mb-3" data-id="element-112">
                  Most Recently: Principal PM at Calo
                </h3>
                <p className="text-navy" data-id="element-113">
                  At Calo, I led personalisation and meal planning algorithms,
                  bringing both nutrition science knowledge and product
                  leadership to create technology that truly improves users'
                  health and wellness. I'm now seeking my next opportunity to
                  apply this unique blend of domain expertise and product
                  leadership at a mission-driven health-tech company.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Skills Section */}
      <section className="py-16 bg-white" data-id="element-114">
        <div className="container mx-auto px-6" data-id="element-115">
          <SectionHeading title="My Skills" subtitle="A unique combination of technical skills and domain expertise" data-id="element-116" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-117">
            <div className="bg-cream rounded-lg p-8" data-id="element-118">
              <div className="flex items-center mb-6" data-id="element-119">
                <div className="w-12 h-12 bg-sage-light rounded-full flex items-center justify-center mr-4 text-sage-dark" data-id="element-120">
                  <CodeIcon size={24} data-id="element-121" />
                </div>
                <h3 className="text-xl font-display font-semibold text-navy-dark" data-id="element-122">
                  Technical & Tools
                </h3>
              </div>
              <ul className="space-y-4" data-id="element-123">
                <li className="flex" data-id="element-124">
                  <span className="text-coral-dark mr-3" data-id="element-125">•</span>
                  <div data-id="element-126">
                    <span className="font-medium text-navy-dark" data-id="element-127">
                      Product Management:
                    </span>
                    <span className="text-navy" data-id="element-128">
                      {' '}
                      Roadmapping, OKRs, Agile/Scrum methodologies
                    </span>
                  </div>
                </li>
                <li className="flex" data-id="element-129">
                  <span className="text-coral-dark mr-3" data-id="element-130">•</span>
                  <div data-id="element-131">
                    <span className="font-medium text-navy-dark" data-id="element-132">
                      Analytics & Data:
                    </span>
                    <span className="text-navy" data-id="element-133">
                      {' '}
                      GA4, server-side tracking, Klaviyo, conversion/retention
                      analysis
                    </span>
                  </div>
                </li>
                <li className="flex" data-id="element-134">
                  <span className="text-coral-dark mr-3" data-id="element-135">•</span>
                  <div data-id="element-136">
                    <span className="font-medium text-navy-dark" data-id="element-137">
                      Design & Research:
                    </span>
                    <span className="text-navy" data-id="element-138">
                      {' '}
                      User journey mapping, customer research, portal/app
                      development
                    </span>
                  </div>
                </li>
                <li className="flex" data-id="element-139">
                  <span className="text-coral-dark mr-3" data-id="element-140">•</span>
                  <div data-id="element-141">
                    <span className="font-medium text-navy-dark" data-id="element-142">
                      Domain Tools:
                    </span>
                    <span className="text-navy" data-id="element-143">
                      {' '}
                      Nutrition algorithms, meal planning engines,
                      personalisation systems
                    </span>
                  </div>
                </li>
                <li className="flex" data-id="element-144">
                  <span className="text-coral-dark mr-3" data-id="element-145">•</span>
                  <div data-id="element-146">
                    <span className="font-medium text-navy-dark" data-id="element-147">
                      Platform Development:
                    </span>
                    <span className="text-navy" data-id="element-148">
                      {' '}
                      ERP systems, production workflow automation, API
                      integrations
                    </span>
                  </div>
                </li>
                <li className="flex" data-id="element-149">
                  <span className="text-coral-dark mr-3" data-id="element-150">•</span>
                  <div data-id="element-151">
                    <span className="font-medium text-navy-dark" data-id="element-152">
                      Certification:
                    </span>
                    <span className="text-navy" data-id="element-153">
                      {' '}
                      Business Analysis Certificate
                    </span>
                  </div>
                </li>
              </ul>
            </div>
            <div className="bg-cream rounded-lg p-8" data-id="element-154">
              <div className="flex items-center mb-6" data-id="element-155">
                <div className="w-12 h-12 bg-sage-light rounded-full flex items-center justify-center mr-4 text-sage-dark" data-id="element-156">
                  <UsersIcon size={24} data-id="element-157" />
                </div>
                <h3 className="text-xl font-display font-semibold text-navy-dark" data-id="element-158">
                  Soft Skills
                </h3>
              </div>
              <ul className="space-y-4" data-id="element-159">
                <li className="flex" data-id="element-160">
                  <span className="text-coral-dark mr-3" data-id="element-161">•</span>
                  <div data-id="element-162">
                    <span className="font-medium text-navy-dark" data-id="element-163">
                      Cross-functional leadership
                    </span>
                    <span className="text-navy" data-id="element-164">
                      {' '}
                      leading product, design, and engineering teams
                    </span>
                  </div>
                </li>
                <li className="flex" data-id="element-165">
                  <span className="text-coral-dark mr-3" data-id="element-166">•</span>
                  <div data-id="element-167">
                    <span className="font-medium text-navy-dark" data-id="element-168">
                      Strategic vision with execution excellence
                    </span>
                    <span className="text-navy" data-id="element-169">
                      {' '}
                      from 0→1 launches to scaling
                    </span>
                  </div>
                </li>
                <li className="flex" data-id="element-170">
                  <span className="text-coral-dark mr-3" data-id="element-171">•</span>
                  <div data-id="element-172">
                    <span className="font-medium text-navy-dark" data-id="element-173">
                      Stakeholder management
                    </span>
                    <span className="text-navy" data-id="element-174">
                      {' '}
                      across commercial, operations, and technical teams
                    </span>
                  </div>
                </li>
                <li className="flex" data-id="element-175">
                  <span className="text-coral-dark mr-3" data-id="element-176">•</span>
                  <div data-id="element-177">
                    <span className="font-medium text-navy-dark" data-id="element-178">
                      Data-driven decision making
                    </span>
                    <span className="text-navy" data-id="element-179">
                      {' '}
                      balancing user needs with business requirements
                    </span>
                  </div>
                </li>
                <li className="flex" data-id="element-180">
                  <span className="text-coral-dark mr-3" data-id="element-181">•</span>
                  <div data-id="element-182">
                    <span className="font-medium text-navy-dark" data-id="element-183">
                      Change management
                    </span>
                    <span className="text-navy" data-id="element-184">
                      {' '}
                      particularly through acquisitions and integrations
                    </span>
                  </div>
                </li>
              </ul>
            </div>
            <div className="bg-cream rounded-lg p-8 md:col-span-2" data-id="element-185">
              <div className="flex items-center mb-6" data-id="element-186">
                <div className="w-12 h-12 bg-sage-light rounded-full flex items-center justify-center mr-4 text-sage-dark" data-id="element-187">
                  <BrainIcon size={24} data-id="element-188" />
                </div>
                <h3 className="text-xl font-display font-semibold text-navy-dark" data-id="element-189">
                  Domain Expertise
                </h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-190">
                <ul className="space-y-4" data-id="element-191">
                  <li className="flex" data-id="element-192">
                    <span className="text-coral-dark mr-3" data-id="element-193">•</span>
                    <div data-id="element-194">
                      <span className="font-medium text-navy-dark" data-id="element-195">
                        Nutrition science
                      </span>
                      <span className="text-navy" data-id="element-196">
                        {' '}
                        (BSc Human Nutrition & Physiology)
                      </span>
                    </div>
                  </li>
                  <li className="flex" data-id="element-197">
                    <span className="text-coral-dark mr-3" data-id="element-198">•</span>
                    <div data-id="element-199">
                      <span className="font-medium text-navy-dark" data-id="element-200">
                        Health & wellness consumer products
                      </span>
                      <span className="text-navy" data-id="element-201"> and user psychology</span>
                    </div>
                  </li>
                  <li className="flex" data-id="element-202">
                    <span className="text-coral-dark mr-3" data-id="element-203">•</span>
                    <div data-id="element-204">
                      <span className="font-medium text-navy-dark" data-id="element-205">
                        Subscription/D2C and B2B business models
                      </span>
                      <span className="text-navy" data-id="element-206"> in wellness space</span>
                    </div>
                  </li>
                </ul>
                <ul className="space-y-4" data-id="element-207">
                  <li className="flex" data-id="element-208">
                    <span className="text-coral-dark mr-3" data-id="element-209">•</span>
                    <div data-id="element-210">
                      <span className="font-medium text-navy-dark" data-id="element-211">
                        Operations-heavy product platforms
                      </span>
                      <span className="text-navy" data-id="element-212"> and automation systems</span>
                    </div>
                  </li>
                  <li className="flex" data-id="element-213">
                    <span className="text-coral-dark mr-3" data-id="element-214">•</span>
                    <div data-id="element-215">
                      <span className="font-medium text-navy-dark" data-id="element-216">
                        Women's health and performance nutrition
                      </span>
                      <span className="text-navy" data-id="element-217">
                        {' '}
                        specific needs and considerations
                      </span>
                    </div>
                  </li>
                  <li className="flex" data-id="element-218">
                    <span className="text-coral-dark mr-3" data-id="element-219">•</span>
                    <div data-id="element-220">
                      <span className="font-medium text-navy-dark" data-id="element-221">
                        Personalisation and algorithmic meal planning
                      </span>
                      <span className="text-navy" data-id="element-222">
                        {' '}
                        for individual health goals
                      </span>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* CTA Section */}
      <section className="py-16 bg-sage" data-id="element-223">
        <div className="container mx-auto px-6 text-center" data-id="element-224">
          <h2 className="text-2xl md:text-3xl font-display font-semibold text-navy-dark mb-6" data-id="element-225">
            Looking for a product leader who understands health-tech?
          </h2>
          <p className="text-navy-dark mb-8 max-w-xl mx-auto" data-id="element-226">
            I'm interested in Lead Product Manager, Head of Product, or Senior
            Product Manager roles at companies building innovative health and
            wellness products with a mission to make wellbeing accessible to
            all.
          </p>
          <Button href="/contact" size="lg" data-id="element-227">
            Contact Me <ArrowRightIcon size={18} className="ml-2" data-id="element-228" />
          </Button>
        </div>
      </section>
    </div>;
}